import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import AIConversation from '../components/AIConversation';

// Helper to convert relative dates like "5 years" or "18 months" to actual dates
const convertToActualDate = (relativeDate) => {
  if (!relativeDate) return '';

  const dateStr = relativeDate.toString().toLowerCase().trim();

  // If it already looks like a date (has month name or year), return as-is
  const months = ['january', 'february', 'march', 'april', 'may', 'june',
                  'july', 'august', 'september', 'october', 'november', 'december',
                  'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  if (months.some(m => dateStr.includes(m)) || /20\d{2}/.test(dateStr)) {
    return relativeDate; // Already a date
  }

  const now = new Date();
  let targetDate = new Date(now);

  // Parse relative formats
  const yearMatch = dateStr.match(/(\d+)\s*years?/);
  const monthMatch = dateStr.match(/(\d+)\s*months?/);

  if (yearMatch) {
    targetDate.setFullYear(targetDate.getFullYear() + parseInt(yearMatch[1]));
  }
  if (monthMatch) {
    targetDate.setMonth(targetDate.getMonth() + parseInt(monthMatch[1]));
  }

  // If we parsed something, return formatted date
  if (yearMatch || monthMatch) {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[targetDate.getMonth()]} ${targetDate.getFullYear()}`;
  }

  // Check for "end of year" type phrases
  if (dateStr.includes('end of year') || dateStr.includes('eoy')) {
    return `December ${now.getFullYear()}`;
  }
  if (dateStr.includes('next year')) {
    return `December ${now.getFullYear() + 1}`;
  }

  return relativeDate; // Return original if we can't parse
};

function QuickStart() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('urls'); // urls, extracting, conversation, distilling, verify-business, verify-products, exit-path, generating, complete
  const [error, setError] = useState('');

  // URL data
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [productUrl, setProductUrl] = useState('');
  const [extractedData, setExtractedData] = useState({});

  // Conversation data
  const [initialMessage, setInitialMessage] = useState('');

  // Verified business data
  const [businessInfo, setBusinessInfo] = useState({
    business_name: '',
    industry: '',
    b2b_b2c: 'B2C',
    primary_market: '',
    years_in_business: '',
    monthly_revenue: '',
    profit_margin: 70,
    core_product: ''
  });

  // Products
  const [products, setProducts] = useState([]);
  const [upgradePercentage, setUpgradePercentage] = useState(20); // % of entry buyers who upgrade to core

  // Exit path
  const [exitPath, setExitPath] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState('');

  useEffect(() => {
    checkAuthAndRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndRestore = async () => {
    const result = await authService.checkAuth();
    if (!result.authenticated) {
      navigate('/login');
      return;
    }

    // Check for saved progress
    try {
      const response = await fetch('/api/quick-start/get-status', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const status = await response.json();

        // If already completed, redirect to quick-start dashboard
        if (status.quick_start_completed) {
          navigate('/quick-start-dashboard');
          return;
        }

        // Restore saved stage
        if (status.quick_start_stage) {
          setStage(status.quick_start_stage);
        }

        // Restore saved data
        if (status.quick_start_data) {
          const data = status.quick_start_data;
          if (data.businessInfo) {
            setBusinessInfo(prev => ({ ...prev, ...data.businessInfo }));
          }
          if (data.products && data.products.length > 0) {
            setProducts(data.products);
          }
          if (data.upgradePercentage !== undefined) {
            setUpgradePercentage(data.upgradePercentage);
          }
          if (data.extractedData) {
            setExtractedData(data.extractedData);
          }
          if (data.exitPath) {
            setExitPath(data.exitPath);
          }
          if (data.targetAmount) {
            setTargetAmount(data.targetAmount);
          }
          if (data.targetDate) {
            setTargetDate(data.targetDate);
          }
          if (data.websiteUrl) {
            setWebsiteUrl(data.websiteUrl);
          }
        }
      }
    } catch (err) {
      console.error('Error restoring progress:', err);
    }

    setLoading(false);
  };

  // Save progress to backend
  const saveProgress = async (newStage, additionalData = {}) => {
    try {
      const dataToSave = {
        businessInfo,
        products,
        upgradePercentage,
        extractedData,
        exitPath,
        targetAmount,
        targetDate,
        websiteUrl,
        ...additionalData
      };

      await fetch('/api/quick-start/save-progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          stage: newStage,
          data: dataToSave
        })
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  // Stage 1: Submit URLs for extraction
  const handleUrlSubmit = async (e) => {
    e.preventDefault();

    // If no URL provided, skip to conversation
    if (!websiteUrl.trim()) {
      setExtractedData({});
      setInitialMessage("Hi! Since you're just getting started, let me ask you a few questions to understand your business.\n\nFirst, what's the name of your business and what do you do?");
      setStage('conversation');
      saveProgress('conversation');
      return;
    }

    setError('');
    setStage('extracting');

    try {
      const response = await fetch('/api/quick-start/extract-urls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          website_url: websiteUrl,
          product_url: productUrl
        })
      });

      const data = await response.json();

      if (response.ok) {
        setExtractedData(data.extracted || {});

        // Start conversation with extracted data
        const convResponse = await fetch('/api/quick-start/start-conversation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            extracted_data: data.extracted || {}
          })
        });

        if (convResponse.ok) {
          const convData = await convResponse.json();
          setInitialMessage(convData.message);
          setStage('conversation');
          saveProgress('conversation', { extractedData: data.extracted || {} });
        } else {
          setError('Failed to start conversation');
          setStage('urls');
        }
      } else {
        setError(data.error || 'Failed to process URLs');
        setStage('urls');
      }
    } catch (err) {
      setError('Error processing URLs: ' + err.message);
      setStage('urls');
    }
  };

  // Handle conversation message
  const handleConversationMessage = async (message, history) => {
    try {
      const response = await fetch('/api/quick-start/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message,
          history,
          extracted_data: extractedData
        })
      });

      if (response.ok) {
        const data = await response.json();
        return {
          message: data.message,
          isComplete: data.isComplete
        };
      } else {
        const errorData = await response.json();
        return { error: errorData.error || 'Failed to get response' };
      }
    } catch (err) {
      return { error: 'Error communicating with AI: ' + err.message };
    }
  };

  // Handle conversation completion
  const handleConversationComplete = async (history) => {
    setStage('distilling');
    setError('');

    try {
      const response = await fetch('/api/quick-start/distill-conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          history,
          extracted_data: extractedData
        })
      });

      if (response.ok) {
        const data = await response.json();
        const bizData = data.business_data || {};

        // Populate business info
        setBusinessInfo({
          business_name: bizData.business_info?.business_name || '',
          industry: bizData.business_info?.industry || '',
          b2b_b2c: bizData.business_info?.b2b_b2c || 'B2C',
          primary_market: bizData.business_info?.primary_market || '',
          years_in_business: bizData.business_info?.years_in_business || '',
          monthly_revenue: bizData.business_info?.monthly_revenue || '',
          profit_margin: bizData.business_info?.profit_margin || 70,
          core_product: extractedData?.core_product || ''
        });

        // Populate products
        let extractedProducts = (bizData.products || []).map((p, i) => ({
          id: `new-${i}`,
          name: p.name || '',
          description: p.description || '',
          price: p.price || 0,
          purchase_type: p.purchase_type || 'one-time',
          position: p.position || 'core',
          profit_margin: p.profit_margin || 70,
          is_primary_tripwire: p.is_primary || false,
          monthly_sales: 0
        }));

        // ALWAYS auto-select cheapest product as primary tripwire
        // The tripwire is the entry-level product that gets customers in the door
        if (extractedProducts.length > 0) {
          // First, clear any AI-set is_primary_tripwire - we'll set it ourselves
          extractedProducts.forEach(p => p.is_primary_tripwire = false);

          // Find the cheapest product with a price > 0
          const productsWithPrice = extractedProducts.filter(p => p.price > 0);
          if (productsWithPrice.length > 0) {
            const cheapestPrice = Math.min(...productsWithPrice.map(p => p.price));
            const cheapestIndex = extractedProducts.findIndex(p => p.price === cheapestPrice);
            if (cheapestIndex >= 0) {
              extractedProducts[cheapestIndex].is_primary_tripwire = true;
              extractedProducts[cheapestIndex].position = 'tripwire';
            }

            // If AI marked something as is_primary (core product), set it as 'core'
            // This is typically the main product, not the tripwire
            extractedProducts.forEach((p, i) => {
              if (p.position !== 'tripwire') {
                // Default non-tripwire products to core or premium based on price
                if (p.price > cheapestPrice * 3) {
                  p.position = 'premium';
                } else {
                  p.position = 'core';
                }
              }
            });
          } else {
            // No products with price, just mark the first one
            extractedProducts[0].is_primary_tripwire = true;
            extractedProducts[0].position = 'tripwire';
          }
        }

        setProducts(extractedProducts.length > 0 ? extractedProducts : [{
          id: 'new-0',
          name: '',
          description: '',
          price: 0,
          purchase_type: 'one-time',
          position: 'core',
          profit_margin: 70,
          is_primary_tripwire: true,
          monthly_sales: 0
        }]);

        // Set exit goal from interview data
        let initialExitPath = '';
        let initialTargetAmount = '';
        let initialTargetDate = '';

        // Try new field names first (exit_goal_type), fall back to old (exit_goal)
        const exitGoalType = bizData.business_info?.exit_goal_type || bizData.business_info?.exit_goal;
        if (exitGoalType) {
          const goalMap = {
            'lifestyle': 'lifestyle',
            'sell': 'build_to_sell',
            'acquisition': 'acquisition',
            'exploring': ''
          };
          initialExitPath = goalMap[exitGoalType] || '';
          setExitPath(initialExitPath);
        }

        // Set target amount from interview
        if (bizData.business_info?.exit_target_amount) {
          // Format as currency if it's just a number
          const amount = bizData.business_info.exit_target_amount;
          if (typeof amount === 'number') {
            initialTargetAmount = `$${amount.toLocaleString()}`;
          } else {
            initialTargetAmount = amount.toString().startsWith('$') ? amount : `$${amount}`;
          }
          setTargetAmount(initialTargetAmount);
        }

        // Set target date/timeline from interview - convert relative dates to actual
        if (bizData.business_info?.exit_target_timeline) {
          initialTargetDate = convertToActualDate(bizData.business_info.exit_target_timeline);
          setTargetDate(initialTargetDate);
        }

        setStage('verify-business');

        // Save progress with the newly parsed data
        saveProgress('verify-business', {
          businessInfo: {
            business_name: bizData.business_info?.business_name || '',
            industry: bizData.business_info?.industry || '',
            b2b_b2c: bizData.business_info?.b2b_b2c || 'B2C',
            primary_market: bizData.business_info?.primary_market || '',
            years_in_business: bizData.business_info?.years_in_business || '',
            monthly_revenue: bizData.business_info?.monthly_revenue || '',
            profit_margin: bizData.business_info?.profit_margin || 70,
            core_product: extractedData?.core_product || ''
          },
          products: extractedProducts.length > 0 ? extractedProducts : [{
            id: 'new-0', name: '', description: '', price: 0, purchase_type: 'one-time',
            position: 'core', profit_margin: 70, is_primary_tripwire: true, monthly_sales: 0
          }],
          exitPath: initialExitPath,
          targetAmount: initialTargetAmount,
          targetDate: initialTargetDate
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to process conversation');
        setStage('conversation');
      }
    } catch (err) {
      setError('Error processing conversation: ' + err.message);
      setStage('conversation');
    }
  };

  // Handle business info changes
  const handleBusinessChange = (field, value) => {
    setBusinessInfo(prev => ({ ...prev, [field]: value }));
  };

  // Handle product changes
  const handleProductChange = (index, field, value) => {
    setProducts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Add product
  const handleAddProduct = () => {
    setProducts(prev => [...prev, {
      id: `new-${Date.now()}`,
      name: '',
      description: '',
      price: 0,
      purchase_type: 'one-time',
      position: 'core',
      profit_margin: 70,
      is_primary_tripwire: false,
      monthly_sales: 0
    }]);
  };

  // Remove product
  const handleRemoveProduct = (index) => {
    if (products.length > 1) {
      setProducts(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Set primary tripwire
  const handleSetPrimary = (index) => {
    setProducts(prev => prev.map((p, i) => ({
      ...p,
      is_primary_tripwire: i === index
    })));
  };

  // Save verified data and go to exit path
  const handleSaveAndContinue = async () => {
    // Validate
    if (!businessInfo.business_name.trim()) {
      setError('Please enter your business name');
      return;
    }

    const validProducts = products.filter(p => p.name.trim() && p.price > 0);
    if (validProducts.length === 0) {
      setError('Please add at least one product with a name and price');
      return;
    }

    setStage('exit-path');
    saveProgress('exit-path');
  };

  // Helper to save and go to verify-products
  const handleGoToProducts = () => {
    setStage('verify-products');
    saveProgress('verify-products');
  };

  // Generate exit strategy
  const handleGenerateStrategy = async () => {
    if (!exitPath) {
      setError('Please select your exit path');
      return;
    }
    if (!targetAmount || !targetDate) {
      setError('Please enter your target amount and date');
      return;
    }

    setError('');
    setStage('generating');

    try {
      // First save all the verified data, including exit goal from the UI
      const saveResponse = await fetch('/api/quick-start/save-verified', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_info: {
            ...businessInfo,
            // Include exit goal data from the UI
            exit_goal_type: exitPath,  // 'lifestyle', 'build_to_sell', etc.
            exit_target_amount: targetAmount,
            exit_target_timeline: targetDate,
            upgrade_percentage: upgradePercentage  // % of entry buyers who upgrade
          },
          products: products.filter(p => p.name.trim()),
          extracted_data: extractedData  // Pass extracted mission/vision from URL
        })
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json();
        throw new Error(errorData.error || 'Failed to save data');
      }

      // Now generate the exit strategy
      const generateResponse = await fetch('/api/exit-strategy/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          exit_path: exitPath,
          target_amount: targetAmount,
          target_date: targetDate
        })
      });

      if (generateResponse.ok) {
        // Mark quick start as completed
        await fetch('/api/business/field', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ field_name: 'quick_start_completed', field_value: 'true' })
        });

        // Navigate to exit strategy page
        navigate('/exit-strategy');
      } else {
        const errorData = await generateResponse.json();
        setError(errorData.error || 'Failed to generate strategy');
        setStage('exit-path');
      }
    } catch (err) {
      setError('Error generating strategy: ' + err.message);
      setStage('exit-path');
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  // Stage 1: URL Entry
  if (stage === 'urls') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-3 text-center">Quick Start</h2>
                <p className="text-muted text-center mb-4">
                  Let's build your Exit Strategy and Battle Plan in about 10-15 minutes.
                  If you have a website, we can scan it to pre-fill information.
                </p>

                <Alert variant="info" className="mb-4">
                  <strong>Don't have a website or sales page yet?</strong> No problem!
                  Just leave the fields below blank and click "Analyze My Business" —
                  we'll gather what we need through a quick conversation instead.
                </Alert>

                {error && <Alert variant="danger">{error}</Alert>}

                <Form onSubmit={handleUrlSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Your Website URL</strong></Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="https://yourbusiness.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      We'll scan your site to pre-fill information (or leave blank to skip)
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Product/Pricing Page URL</strong> (optional)</Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="https://yourbusiness.com/pricing"
                      value={productUrl}
                      onChange={(e) => setProductUrl(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      If you have a specific pricing or product page, we can extract more details
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid">
                    <Button type="submit" size="lg" className="btn-brand-primary">
                      Analyze My Business →
                    </Button>
                  </div>
                </Form>

                <div className="text-center mt-4">
                  <Button
                    variant="link"
                    className="text-muted"
                    onClick={() => navigate('/welcome')}
                  >
                    ← Back to path selection
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 2: Extracting (loading)
  if (stage === 'extracting') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow p-5">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem', margin: '0 auto' }} />
              <h4>Analyzing Your Website...</h4>
              <p className="text-muted mt-3">
                We're scanning your site to learn about your business, products, and pricing.
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 3: AI Conversation
  if (stage === 'conversation') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <AIConversation
              title="Let's Learn About Your Business"
              subtitle="A quick conversation to gather what we need for your exit strategy"
              initialMessage={initialMessage}
              onSendMessage={handleConversationMessage}
              onComplete={handleConversationComplete}
              onCancel={() => setStage('urls')}
              maxExchanges={8}
              doneButtonText="I'm Done - Build My Strategy"
              placeholder="Share your thoughts..."
            />
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 4: Distilling (loading)
  if (stage === 'distilling') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow p-5">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem', margin: '0 auto' }} />
              <h4>Processing Your Information...</h4>
              <p className="text-muted mt-3">
                Organizing everything we learned into your business profile.
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 5: Verify Business Info
  if (stage === 'verify-business') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h3 className="mb-3">Verify Your Business Information</h3>
                <p className="text-muted mb-4">
                  Please review and correct any information below. This data will be used to build your exit strategy.
                </p>

                {error && <Alert variant="danger">{error}</Alert>}

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Business Name *</strong></Form.Label>
                      <Form.Control
                        type="text"
                        value={businessInfo.business_name}
                        onChange={(e) => handleBusinessChange('business_name', e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Industry</strong></Form.Label>
                      <Form.Control
                        type="text"
                        value={businessInfo.industry}
                        onChange={(e) => handleBusinessChange('industry', e.target.value)}
                        placeholder="e.g., Marketing, SaaS, E-commerce"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Business Type</strong></Form.Label>
                      <Form.Select
                        value={businessInfo.b2b_b2c}
                        onChange={(e) => handleBusinessChange('b2b_b2c', e.target.value)}
                      >
                        <option value="B2B">B2B (Business to Business)</option>
                        <option value="B2C">B2C (Business to Consumer)</option>
                        <option value="Both">Both B2B and B2C</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Years in Business</strong></Form.Label>
                      <Form.Control
                        type="text"
                        value={businessInfo.years_in_business}
                        onChange={(e) => handleBusinessChange('years_in_business', e.target.value)}
                        placeholder="e.g., 2 years, Pre-launch"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Who Do You Serve?</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={businessInfo.primary_market}
                    onChange={(e) => handleBusinessChange('primary_market', e.target.value)}
                    placeholder="Describe your target market..."
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Current Monthly Revenue</strong></Form.Label>
                      <Form.Control
                        type="number"
                        value={businessInfo.monthly_revenue}
                        onChange={(e) => handleBusinessChange('monthly_revenue', e.target.value)}
                        placeholder="e.g., 5000"
                      />
                      <Form.Text className="text-muted">Enter 0 if pre-revenue</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Profit Margin %</strong></Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        value={businessInfo.profit_margin}
                        onChange={(e) => handleBusinessChange('profit_margin', e.target.value)}
                      />
                      <Form.Text className="text-muted">% you keep after all costs</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between mt-4">
                  <Button variant="outline-secondary" onClick={() => setStage('conversation')}>
                    ← Back to Conversation
                  </Button>
                  <Button className="btn-brand-primary" onClick={handleGoToProducts}>
                    Continue to Products →
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 6: Verify Products
  if (stage === 'verify-products') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h3 className="mb-3">Verify Your Products & Services</h3>
                <p className="text-muted mb-4">
                  Review your products below. The <strong>Entry Point</strong> (tripwire) is your lowest-priced offer that gets
                  new customers in the door. We've auto-selected your cheapest product as the entry point.
                </p>

                {error && <Alert variant="danger">{error}</Alert>}

                <Table responsive>
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Price</th>
                      <th>Type</th>
                      <th>Position</th>
                      <th>Entry Point</th>
                      <th>Profit %</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, index) => (
                      <tr key={product.id}>
                        <td>
                          <Form.Control
                            type="text"
                            value={product.name}
                            onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                            placeholder="Product name"
                          />
                        </td>
                        <td style={{ width: '100px' }}>
                          <Form.Control
                            type="number"
                            min="0"
                            value={product.price}
                            onChange={(e) => handleProductChange(index, 'price', parseFloat(e.target.value) || 0)}
                            style={!product.price ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                          />
                        </td>
                        <td style={{ width: '130px' }}>
                          <Form.Select
                            value={product.purchase_type}
                            onChange={(e) => handleProductChange(index, 'purchase_type', e.target.value)}
                          >
                            <option value="one-time">One-time</option>
                            <option value="recurring-monthly">Monthly</option>
                            <option value="recurring-annual">Annual</option>
                          </Form.Select>
                        </td>
                        <td style={{ width: '120px' }}>
                          <Form.Select
                            value={product.position || 'core'}
                            onChange={(e) => handleProductChange(index, 'position', e.target.value)}
                          >
                            <option value="entry">🎣 Entry/Tripwire</option>
                            <option value="core">⭐ Core</option>
                            <option value="upsell">⬆️ Upsell</option>
                            <option value="cross-sell">↔️ Cross-sell</option>
                            <option value="premium">💎 Premium</option>
                            <option value="downsell">⬇️ Downsell</option>
                          </Form.Select>
                        </td>
                        <td style={{ width: '90px' }} className="text-center">
                          {product.is_primary_tripwire ? (
                            <Badge bg="success">Entry</Badge>
                          ) : (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleSetPrimary(index)}
                              title="Set as entry-level product (tripwire)"
                            >
                              Set Entry
                            </Button>
                          )}
                        </td>
                        <td style={{ width: '80px' }}>
                          <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            value={product.profit_margin}
                            onChange={(e) => handleProductChange(index, 'profit_margin', parseFloat(e.target.value) || 0)}
                          />
                        </td>
                        <td style={{ width: '50px' }}>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveProduct(index)}
                            disabled={products.length === 1}
                          >
                            X
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Button variant="outline-primary" size="sm" onClick={handleAddProduct}>
                  + Add Product
                </Button>

                {/* Missing price warning */}
                {products.some(p => p.name.trim() && !p.price) && (
                  <Alert variant="danger" className="mt-3 d-flex align-items-center">
                    <strong className="me-2">⚠ Missing Price:</strong>
                    {products.filter(p => p.name.trim() && !p.price).map(p => p.name || 'Unnamed').join(', ')}
                    {' '} — please add a price above so we can calculate your revenue projections accurately.
                  </Alert>
                )}

                {/* Upgrade Percentage Section */}
                {products.filter(p => p.position === 'core' || p.position === 'premium').length > 0 && (
                  <Card className="mt-4 bg-light">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col md={8}>
                          <h6 className="mb-1">Expected Upgrade Rate</h6>
                          <p className="text-muted small mb-0">
                            What percentage of customers who buy your entry-level product will upgrade to your core/premium products?
                            (Industry average is 10-30%)
                          </p>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <div className="d-flex align-items-center">
                              <Form.Control
                                type="number"
                                min="1"
                                max="100"
                                value={upgradePercentage}
                                onChange={(e) => setUpgradePercentage(Math.min(100, Math.max(1, parseInt(e.target.value) || 20)))}
                                style={{ width: '80px' }}
                              />
                              <span className="ms-2">%</span>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button variant="outline-secondary" onClick={() => setStage('verify-business')}>
                    ← Back to Business Info
                  </Button>
                  <Button className="btn-brand-primary" onClick={handleSaveAndContinue}>
                    Continue to Exit Path →
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 7: Exit Path Selection
  if (stage === 'exit-path') {
    const exitOptions = [
      {
        value: 'lifestyle',
        title: 'Lifestyle Business',
        description: 'Build sustainable income while maintaining freedom and work-life balance'
      },
      {
        value: 'build_to_sell',
        title: 'Build to Sell',
        description: 'Maximize business value for eventual sale to another company or investor'
      },
      {
        value: 'acquisition',
        title: 'Strategic Acquisition',
        description: 'Position for acquisition by a larger company in your industry'
      },
      {
        value: 'legacy',
        title: 'Legacy/Family Business',
        description: 'Build a lasting enterprise to pass on to future generations'
      }
    ];

    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h3 className="mb-3">Choose Your Exit Path</h3>
                <p className="text-muted mb-4">
                  What's your vision for this business? This determines how we structure your strategy.
                </p>

                {error && <Alert variant="danger">{error}</Alert>}

                <Row className="mb-4">
                  {exitOptions.map((option) => (
                    <Col md={6} key={option.value} className="mb-3">
                      <Card
                        className={`h-100 cursor-pointer ${exitPath === option.value ? 'border-primary bg-light' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExitPath(option.value)}
                      >
                        <Card.Body>
                          <Form.Check
                            type="radio"
                            id={option.value}
                            name="exitPath"
                            checked={exitPath === option.value}
                            onChange={() => setExitPath(option.value)}
                            label={<strong>{option.title}</strong>}
                          />
                          <p className="text-muted small mb-0 ms-4">{option.description}</p>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Target Exit/Revenue Amount</strong></Form.Label>
                      <Form.Control
                        type="text"
                        value={targetAmount}
                        onChange={(e) => setTargetAmount(e.target.value)}
                        placeholder="e.g., $1,000,000"
                      />
                      <Form.Text className="text-muted">
                        What's your financial goal?
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Target Date</strong></Form.Label>
                      <Form.Control
                        type="text"
                        value={targetDate}
                        onChange={(e) => setTargetDate(e.target.value)}
                        placeholder="e.g., December 2028"
                      />
                      <Form.Text className="text-muted">
                        When do you want to reach this goal?
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between mt-4">
                  <Button variant="outline-secondary" onClick={handleGoToProducts}>
                    ← Back to Products
                  </Button>
                  <Button
                    size="lg"
                    className="btn-brand-success"
                    onClick={handleGenerateStrategy}
                    disabled={!exitPath || !targetAmount || !targetDate}
                  >
                    Generate My Exit Strategy →
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Stage 8: Generating (loading)
  if (stage === 'generating') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow p-5">
              <Spinner animation="border" variant="success" className="mb-3" style={{ width: '3rem', height: '3rem', margin: '0 auto' }} />
              <h4>Building Your Exit Strategy & Battle Plan...</h4>
              <p className="text-muted mt-3">
                Crunching the numbers and creating your personalized roadmap to {targetAmount}.
              </p>
              <p className="text-muted small">
                This may take 30-60 seconds...
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Default fallback
  return (
    <Container className="py-5 text-center">
      <p>Loading...</p>
    </Container>
  );
}

export default QuickStart;
